import React from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    IconButton,
    Typography,
    Box,
    Tooltip,
    CircularProgress,
    useMediaQuery,
    useTheme
} from '@mui/material';
import {
    Close,
    DownloadOutlined,
    ErrorOutlineOutlined,
    OpenInNewOutlined,
    PictureAsPdfOutlined
} from '@mui/icons-material';

interface PdfPreviewModalProps {
    open: boolean;
    onClose: () => void;
    fileUrl: string;
    fileName: string;
}

const PdfPreviewModal: React.FC<PdfPreviewModalProps> = ({ open, onClose, fileUrl, fileName }) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const [isPreviewLoading, setIsPreviewLoading] = React.useState(true);
    const [hasPreviewError, setHasPreviewError] = React.useState(false);

    React.useEffect(() => {
        if (open) {
            setIsPreviewLoading(Boolean(fileUrl));
            setHasPreviewError(false);
        }
    }, [fileUrl, open]);

    const handleDownload = () => {
        const link = document.createElement('a');
        link.href = fileUrl;
        link.download = fileName || 'relatorio.pdf';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const handleOpenInNewTab = () => {
        window.open(fileUrl, '_blank', 'noopener,noreferrer');
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="lg"
            fullWidth
            fullScreen={isMobile}
            aria-labelledby="pdf-preview-title"
            PaperProps={{
                className: "!tw-overflow-hidden tw-shadow-2xl tw-transition-all tw-duration-300 tw-ease-gtr-smooth sm:!tw-rounded-2xl",
                sx: {
                    height: isMobile ? '100dvh' : '90vh',
                    borderRadius: isMobile ? 0 : 2,
                    backgroundColor: '#f3f4f6'
                }
            }}
        >
            <DialogTitle
                className="tw-bg-gradient-to-r tw-from-gtr-teal-dark tw-to-gtr-teal"
                sx={{
                    m: 0,
                    px: { xs: 2, sm: 3 },
                    py: { xs: 1.5, sm: 2 },
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    gap: 2,
                    color: '#fff',
                    fontFamily: 'Prometo'
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0 }}>
                    <Box
                        className="tw-flex tw-h-12 tw-w-12 tw-flex-none tw-items-center tw-justify-center tw-rounded-xl tw-bg-white/10 tw-shadow-inner tw-ring-1 tw-ring-white/10"
                    >
                        <PictureAsPdfOutlined sx={{ fontSize: 28 }} />
                    </Box>
                    <Box sx={{ minWidth: 0 }}>
                        <Typography
                            id="pdf-preview-title"
                            variant="h6"
                            component="div"
                            noWrap
                            sx={{ fontWeight: 700, lineHeight: 1.2 }}
                        >
                            Visualização do relatório
                        </Typography>
                        <Typography
                            variant="body2"
                            noWrap
                            sx={{ mt: 0.5, color: 'rgba(255, 255, 255, 0.78)' }}
                        >
                            Confira o documento antes de abrir ou baixar
                        </Typography>
                    </Box>
                </Box>
                <Tooltip title="Fechar" arrow placement="left">
                    <IconButton
                        aria-label="Fechar visualização do relatório"
                        onClick={onClose}
                        className="tw-transition-all tw-duration-200 hover:tw-rotate-90 hover:tw-bg-white/10"
                        sx={{ color: '#fff', flexShrink: 0, border: '1px solid rgba(255, 255, 255, 0.12)' }}
                    >
                        <Close />
                    </IconButton>
                </Tooltip>
            </DialogTitle>

            <DialogContent
                sx={{
                    p: { xs: 1, sm: 2 },
                    display: 'flex',
                    flex: 1,
                    minHeight: 0,
                    overflow: 'hidden',
                    bgcolor: '#f3f4f6'
                }}
            >
                <Box
                    className="tw-relative tw-h-full tw-w-full tw-overflow-hidden tw-rounded-xl tw-border tw-border-slate-200 tw-bg-white tw-shadow-sm"
                >
                    {fileUrl && !hasPreviewError && (
                        <iframe
                            src={fileUrl}
                            title={fileName || 'Pré-visualização do relatório em PDF'}
                            width="100%"
                            height="100%"
                            onLoad={() => setIsPreviewLoading(false)}
                            onError={() => {
                                setIsPreviewLoading(false);
                                setHasPreviewError(true);
                            }}
                            className={`tw-border-0 tw-transition-opacity tw-duration-300 ${isPreviewLoading ? 'tw-opacity-0' : 'tw-opacity-100'}`}
                        />
                    )}

                    {(isPreviewLoading || !fileUrl) && !hasPreviewError && (
                        <Box className="tw-absolute tw-inset-0 tw-flex tw-flex-col tw-items-center tw-justify-center tw-gap-3 tw-bg-white">
                            <CircularProgress size={36} sx={{ color: '#006C77' }} />
                            <Typography sx={{ color: '#44515c', fontWeight: 600 }}>
                                Preparando visualização do PDF...
                            </Typography>
                        </Box>
                    )}

                    {hasPreviewError && (
                        <Box className="tw-absolute tw-inset-0 tw-flex tw-flex-col tw-items-center tw-justify-center tw-gap-3 tw-p-6 tw-text-center">
                            <Box className="tw-flex tw-h-14 tw-w-14 tw-items-center tw-justify-center tw-rounded-full tw-bg-red-50 tw-text-red-500">
                                <ErrorOutlineOutlined sx={{ fontSize: 32 }} />
                            </Box>
                            <Typography sx={{ color: '#263238', fontWeight: 700 }}>
                                Não foi possível exibir o PDF
                            </Typography>
                            <Typography variant="body2" sx={{ color: '#66717b' }}>
                                Você ainda pode abrir o arquivo em uma nova guia ou fazer o download.
                            </Typography>
                        </Box>
                    )}
                </Box>
            </DialogContent>

            <DialogActions
                sx={{
                    px: { xs: 1.5, sm: 2.5 },
                    py: 1.5,
                    bgcolor: '#fff',
                    borderTop: '1px solid #e5e7eb',
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: 1,
                    '& > :not(style) ~ :not(style)': { ml: 0 }
                }}
            >
                <Tooltip title={fileName || 'relatorio.pdf'} arrow placement="top">
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexGrow: 1, minWidth: 0 }}>
                        <Box className="tw-flex tw-h-9 tw-w-9 tw-flex-none tw-items-center tw-justify-center tw-rounded-lg tw-bg-red-50 tw-text-red-500">
                            <PictureAsPdfOutlined fontSize="small" />
                        </Box>
                        <Box sx={{ minWidth: 0 }}>
                            <Typography variant="caption" sx={{ display: 'block', color: '#7a858e', lineHeight: 1 }}>
                                Arquivo gerado
                            </Typography>
                            <Typography
                                variant="body2"
                                noWrap
                                sx={{ mt: 0.25, maxWidth: { xs: 170, sm: 280 }, color: '#263238', fontWeight: 600 }}
                            >
                                {fileName || 'relatorio.pdf'}
                            </Typography>
                        </Box>
                    </Box>
                </Tooltip>
                <Button
                    onClick={handleOpenInNewTab}
                    disabled={!fileUrl}
                    variant="contained"
                    startIcon={<OpenInNewOutlined />}
                    className="tw-transition-all tw-duration-200 hover:-tw-translate-y-0.5 hover:tw-shadow-lg"
                    sx={{
                        background: 'rgb(3, 156, 245)',
                        '&:hover': { background: '#0288d1' },
                        fontFamily: 'Prometo',
                        minWidth: { sm: 156 },
                        minHeight: 44,
                        flexGrow: { xs: 1, sm: 0 },
                        whiteSpace: 'nowrap'
                    }}
                >
                    Abrir em nova guia
                </Button>
                <Button
                    onClick={handleDownload}
                    disabled={!fileUrl}
                    variant="contained"
                    startIcon={<DownloadOutlined />}
                    className="tw-transition-all tw-duration-200 hover:-tw-translate-y-0.5 hover:tw-shadow-lg"
                    sx={{
                        background: '#00B700',
                        '&:hover': { background: '#008a00' },
                        fontFamily: 'Prometo',
                        minWidth: { sm: 132 },
                        minHeight: 44,
                        flexGrow: { xs: 1, sm: 0 }
                    }}
                >
                    Download
                </Button>
                <Button
                    onClick={onClose}
                    variant="contained"
                    startIcon={<Close />}
                    className="tw-transition-all tw-duration-200 hover:-tw-translate-y-0.5 hover:tw-shadow-lg"
                    sx={{
                        background: 'rgb(255, 69, 96)',
                        '&:hover': { background: '#d32f2f' },
                        fontFamily: 'Prometo',
                        minWidth: { sm: 116 },
                        minHeight: 44,
                        flexGrow: { xs: 1, sm: 0 }
                    }}
                >
                    Fechar
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default PdfPreviewModal;
